import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import { del, get, set } from "idb-keyval";
import { getCacheUserId } from "#user";

type AsyncStoragePersister = ReturnType<typeof createAsyncStoragePersister>;

const TYPE_MARKER = "\x00type";
const DATE_TYPE = "date";

// Default TanStack Query serializer that preserves Date instances through
// JSON.stringify so they round-trip back to Date in deserializeQueryData.
export function serializeQueryData(data: unknown): string {
  return JSON.stringify(data, function (key, value) {
    if (this[key] instanceof Date) {
      return {
        [TYPE_MARKER]: DATE_TYPE,
        value: this[key].toISOString(),
      };
    } else {
      return value;
    }
  });
}

export function deserializeQueryData(text: string): unknown {
  return JSON.parse(text, (key, value) => {
    if (typeof value === "object" && value?.[TYPE_MARKER] === DATE_TYPE) {
      return new Date(value.value);
    } else {
      return value;
    }
  });
}

// Impersonation sessions are never persisted (ADR-0004): the offline
// IndexedDB cache is a product requirement for the *user's own* data, but
// while a site admin acts as someone else, persisting would write the
// customer's data — and the impersonated identity itself — into the cache
// slot on the admin's machine, where a later restore resurrects it after
// the impersonation has ended. Set from the /api/me result (the only
// source of truth for "who is this session"), enforced at the persister's
// write (not dehydration, which would persist an *empty* client and wipe
// the admin's own offline slot). Restores are unaffected.
let persistenceSuspended = false;

export function setPersistenceSuspended(suspended: boolean): void {
  persistenceSuspended = suspended;
}

export function isPersistenceSuspended(): boolean {
  return persistenceSuspended;
}

export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 15,
        gcTime: Infinity,
      },
      dehydrate: {
        shouldDehydrateQuery: ({ meta }) => meta?.persist !== false,
        serializeData: serializeQueryData,
      },
      hydrate: {
        deserializeData: deserializeQueryData,
      },
    },
  });
}

// Persister backed by IndexedDB via idb-keyval. The persister `key` captures
// the user id at creation time so each PersistQueryClientProvider is bound to
// a single cache slot, but the storage callbacks re-read the current
// `getCacheUserId()` on every operation so that mid-session user switches
// write to the new user's slot without remounting.
export function createIdbPersister(cacheUserId: string): AsyncStoragePersister {
  return createAsyncStoragePersister({
    key: `REACT_QUERY_OFFLINE_CACHE:${cacheUserId}`,
    storage: {
      getItem: async (key) => {
        const value = await get(`${key}:${getCacheUserId()}`);
        return value ?? null;
      },
      setItem: async (key, value) => {
        // No writes at all during an impersonation session — neither the
        // customer's data nor an empty overwrite of the admin's own slot.
        if (persistenceSuspended) {
          return;
        }
        await set(`${key}:${getCacheUserId()}`, value);
      },
      removeItem: async (key) => {
        await del(`${key}:${getCacheUserId()}`);
      },
    },
  });
}
